import { useEffect, useState } from 'react'

export type SystemStatus = 'operational' | 'degraded' | 'down' | 'checking'

export interface ServiceStatus {
  name: string
  status: 'operational' | 'degraded' | 'down'
  responseTime?: number
  message?: string
}

export interface StatusData {
  overall: SystemStatus
  services: ServiceStatus[]
  timestamp: string
}

interface UseSystemStatusOptions {
  refreshInterval?: number // in milliseconds
  enabled?: boolean
}

export function useSystemStatus(options: UseSystemStatusOptions = {}) {
  const { refreshInterval = 60000, enabled = true } = options // Default: 60 seconds
  const [status, setStatus] = useState<StatusData>({
    overall: 'checking',
    services: [],
    timestamp: new Date().toISOString(),
  })
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!enabled) return

    const checkStatus = async () => {
      try {
        const response = await fetch('/api/status', {
          cache: 'no-store',
        })

        if (response.ok) {
          const data = await response.json()
          setStatus(data)
        } else {
          // API endpoint itself is down
          setStatus({
            overall: 'down',
            services: [
              {
                name: 'Status API',
                status: 'down',
                message: `HTTP ${response.status}`,
              },
            ],
            timestamp: new Date().toISOString(),
          })
        }
      } catch (error) {
        // Network error or timeout
        setStatus({
          overall: 'down',
          services: [
            {
              name: 'Status API',
              status: 'down',
              message: 'Network error',
            },
          ],
          timestamp: new Date().toISOString(),
        })
      } finally {
        setIsLoading(false)
      }
    }

    // Initial check
    checkStatus()

    // Set up interval for periodic checks
    const intervalId = setInterval(checkStatus, refreshInterval)

    // Cleanup
    return () => clearInterval(intervalId)
  }, [refreshInterval, enabled])

  return { status, isLoading }
}
